import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@shared/utils/cn";

type Ctx = {
  open: boolean;
  setOpen: (v: boolean) => void;
  anchor: HTMLElement | null;
  setAnchor: (el: HTMLElement | null) => void;
};
const PopoverCtx = React.createContext<Ctx | null>(null);

function Popover({ open, onOpenChange, children }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  children: React.ReactNode;
}) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const ctx = React.useMemo<Ctx>(() => ({ open, setOpen: onOpenChange, anchor, setAnchor }), [open, onOpenChange, anchor]);
  return <PopoverCtx.Provider value={ctx}>{children}</PopoverCtx.Provider>;
}

function PopoverTrigger({ asChild, children }: { asChild?: boolean; children: React.ReactElement<any> }) {
  const ctx = React.useContext(PopoverCtx);
  if (!ctx) return children;
  const { open, setOpen, setAnchor } = ctx;

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget as HTMLElement);
    setOpen(!open);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: (e: any) => {
        (children.props as any)?.onClick?.(e);
        onClick(e);
      },
    } as any) as typeof children;
  }

  return (
    <button onClick={onClick}>
      {children}
    </button>
  );
}

function useAnchorPosition(anchor: HTMLElement | null, align: "start" | "end") {
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  React.useLayoutEffect(() => {
    const update = () => {
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      let left = rect.left;
      if (align === "end") left = rect.right;
      setPos({ top: rect.bottom + 6, left, width: rect.width });
    };
    update();
    if (!anchor) return;
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor, align]);
  return pos;
}

function PopoverContent({ id, className, align = "start", children, draggable = false, dragHandleSelector = '[data-popover-drag-handle]', style, closeOnOutsideClick = true }: {
  id?: string;
  className?: string;
  align?: "start" | "end";
  children: React.ReactNode;
  /** Enable dragging the popover via a handle inside */
  draggable?: boolean;
  /** CSS selector for the handle element inside content */
  dragHandleSelector?: string;
  /** Optional inline style to override position/size */
  style?: React.CSSProperties;
  /** Whether clicks outside should close the popover */
  closeOnOutsideClick?: boolean;
}) {
  const ctx = React.useContext(PopoverCtx);
  const container = typeof document !== "undefined" ? document.body : null;
  const { open, setOpen, anchor } = ctx ?? ({} as Ctx);
  const pos = useAnchorPosition(anchor ?? null, align);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const offsetRef = React.useRef(offset);
  React.useEffect(() => { offsetRef.current = offset; }, [offset]);

  // Reset offset when anchor changes or popover reopens
  React.useEffect(() => {
    if (!open) return;
    setOffset({ x: 0, y: 0 });
  }, [anchor, open]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen?.(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  // Keep content within viewport bounds (best effort)
  React.useLayoutEffect(() => {
    if (!open) return;
    const el = contentRef.current;
    if (!el) return;
    if (typeof window === "undefined") return;

    const rect = el.getBoundingClientRect();
    const margin = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxLeft = Math.max(margin, viewportWidth - margin - rect.width);
    const minLeft = Math.min(margin, maxLeft);
    const maxTop = Math.max(margin, viewportHeight - margin - rect.height);
    const minTop = Math.min(margin, maxTop);

    const clampedLeft = Math.min(Math.max(rect.left, minLeft), maxLeft);
    const clampedTop = Math.min(Math.max(rect.top, minTop), maxTop);

    const dx = clampedLeft - rect.left;
    const dy = clampedTop - rect.top;

    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    }
  }, [open, pos.top, pos.left, offset.x, offset.y, style]);

  // Dragging behavior
  React.useEffect(() => {
    if (!draggable || !open) return;
    const el = contentRef.current;
    if (!el) return;
    const handle = el.querySelector(dragHandleSelector) as HTMLElement | null;
    if (!handle) return;

    const state = { pressed: false, dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // Ignore interactive targets so buttons/links remain clickable
      const target = e.target as HTMLElement | null;
      if (target && (target.closest('button, a, [role="button"], [data-no-drag], [data-popover-no-drag]'))) {
        state.pressed = false;
        state.dragging = false;
        return;
      }
      state.pressed = true;
      state.dragging = false;
      state.startX = e.clientX;
      state.startY = e.clientY;
      state.baseX = offsetRef.current.x;
      state.baseY = offsetRef.current.y;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!state.pressed) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (!state.dragging) {
        // Threshold to distinguish click vs drag
        if ((dx * dx + dy * dy) < 9) return; // ~3px
        state.dragging = true;
        document.body.style.userSelect = 'none';
      }
      setOffset({ x: state.baseX + dx, y: state.baseY + dy });
    };
    const onPointerUp = (e: PointerEvent) => {
      if (!state.pressed) return;
      state.pressed = false;
      state.dragging = false;
      document.body.style.userSelect = '';
    };

    handle.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      document.body.style.userSelect = '';
    };
  }, [draggable, dragHandleSelector, open]);

  if (!container || !open) return null;

  return createPortal(
    <div
      className={cn("fixed inset-0 z-[70]", closeOnOutsideClick ? undefined : "pointer-events-none")}
      onClick={closeOnOutsideClick ? () => setOpen?.(false) : undefined}
    >
      <div
        className={cn(
          "bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 border shadow-md",
          "rounded-md p-3 w-80", // default width; overridden by className
          closeOnOutsideClick ? undefined : "pointer-events-auto",
          className
        )}
        id={id}
        ref={contentRef}
        style={{ position: "fixed", top: pos.top + offset.y, left: pos.left + offset.x, ...(style || {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    container
  );
}

export { Popover, PopoverTrigger, PopoverContent };
